import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { track } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const StoreContext = createContext(null);
const CART_KEY = "luxyreign_cart";
const WISH_KEY = "luxyreign_wishlist";

export function StoreProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem(CART_KEY) || "[]"));
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem(WISH_KEY) || "[]"));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)); }, [wishlist]);

  // Sync wishlist with server when authenticated
  useEffect(() => {
    if (!user || user === false) return;
    (async () => {
      try {
        const localIds = JSON.parse(localStorage.getItem(WISH_KEY) || "[]");
        for (const id of localIds) { try { await api.post(`/wishlist/${id}`); } catch {} }
        const { data } = await api.get("/wishlist");
        setWishlist(data.map((p) => p.id));
      } catch {}
    })();
  }, [user]);

  const addToCart = useCallback((product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        toast.info("This piece is already in your bag");
        return prev;
      }
      track("add_to_bag", { product_id: product.id });
      toast.success("Added to your bag");
      return [...prev, {
        product_id: product.id, quantity: 1,
        snapshot: { title: product.title, brand: product.brand, price: product.price,
          image: (product.images || [])[0] || "", slug: product.slug || product.id, currency: product.currency },
      }];
    });
    setCartOpen(true);
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const inCart = useCallback((id) => cart.some((i) => i.product_id === id), [cart]);

  const toggleWishlist = useCallback(async (product) => {
    const id = product.id;
    const has = wishlist.includes(id);
    setWishlist((prev) => (has ? prev.filter((x) => x !== id) : [...prev, id]));
    track(has ? "wishlist_remove" : "wishlist_add", { product_id: id });
    if (user && user !== false) {
      try { has ? await api.delete(`/wishlist/${id}`) : await api.post(`/wishlist/${id}`); } catch {}
    }
    toast.success(has ? "Removed from wishlist" : "Saved to wishlist");
  }, [wishlist, user]);

  const inWishlist = useCallback((id) => wishlist.includes(id), [wishlist]);

  const cartSubtotal = cart.reduce((s, i) => s + (i.snapshot?.price || 0) * i.quantity, 0);

  return (
    <StoreContext.Provider value={{
      cart, setCart, addToCart, removeFromCart, clearCart, inCart, cartSubtotal,
      cartOpen, setCartOpen, wishlist, toggleWishlist, inWishlist, setWishlist,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => useContext(StoreContext);
